import React from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { useT } from "@/lib/LanguageContext";
import { tenantSiteUrl } from "@/lib/tenantHost";
import { AdminSidebar } from "./layout/AdminSidebar";
import { SuperAdminBanner } from "./layout/SuperAdminBanner";
import { tenantDisplayName } from "../super_admin/tenantDisplay";
import { ConversationsProvider, useConversations } from "./ConversationsContext";
import { Menu, X, OctagonAlert, AlertTriangle, Clock } from "lucide-react";
import { TRIAL_PATHS } from "../saas/trial/contract";
import { adminHref } from "./adminPath";
import EntitlementsProvider from "./entitlements/EntitlementsProvider";
// crm-module BEGIN C3.02
import { normaliseRole } from "./crm/roles";
// crm-module END

/**
 * The subscription state of the practice, on every page of the console.
 *
 * A practice suspended for non-payment still answers READS, so the dashboard looked
 * completely normal while every write came back 403 TENANT_SUSPENDED (BUG-041): the
 * practitioner edited, saved, saw nothing happen, and had no statement of the cause. The
 * dunning banner that already existed lives inside BillingAdmin.jsx — visible only to
 * someone who had already guessed the answer. The layout is the only surface that is on
 * screen everywhere, and it is shared by the desktop and mobile trees, so the bar renders
 * exactly once.
 *
 * `past_due` is a WARNING (nothing is cut yet, the practice is still online) and
 * `suspended` is a STATEMENT OF FACT (it is already cut); showing the same wording for
 * both would misstate one of them.
 *
 * A `manual` suspension — and equally one with NO reason recorded, which
 * `tenant_middleware.tenant_login_blocked` reads as a sanction — is deliberately silent.
 * It is a platform-side decision, the owner cannot even log in while it holds, and
 * pointing them at billing for a moderation decision would send them to pay for something
 * payment cannot fix.
 *
 * Absence of `status` is treated as active: the field is not guaranteed on every payload,
 * and inventing an alarm out of a missing key would be worse than the bug.
 *
 * `trial` is `my-tenant.trial` (the wire contract's read model,
 * `docs/payment-module/saas-trial-period.md` §2). This shell banner shows it ONLY while
 * `trialing`, and deliberately carries no date: it renders on every page and this
 * component is covered by suites that mock `useT` alone, so the day count is the one
 * server-owned fact it states ("{days} day(s) left"), never a date it would have to
 * compute itself. Precedence is suspended > past_due > trialing — a practice already cut
 * off, or about to be, has nothing to learn from being told its trial is still running.
 * `days_remaining` absent or non-integer while trialing renders nothing (the invariant
 * says this should not happen, but a banner cannot say "undefined-day trial"); exactly `0`
 * gets its own last-day sentence.
 */
const AccountStatusBanner = ({ status, reason, trial }) => {
  const t = useT("admin");
  const daysLeft = trial?.days_remaining;
  const banner =
    // Only the non-payment cut-off gets this banner. An ABSENT reason is a sanction, not
    // dunning — `tenant_middleware.tenant_login_blocked` reads it that way and blocks the
    // owner's login upstream, so it cannot reach a rendered console; claiming non-payment
    // for it was the opposite fail-safe to the backend's.
    status === "suspended" && reason === "non_payment"
      ? {
          testid: "admin-suspended-banner",
          tone: "bg-rose-100 text-rose-900 border-rose-300",
          Icon: OctagonAlert,
          text: t("acct.suspended.text"),
          cta: t("acct.billingCta"),
        }
      : status === "past_due"
        ? {
            testid: "admin-pastdue-banner",
            tone: "bg-amber-100 text-amber-900 border-amber-300",
            Icon: AlertTriangle,
            text: t("acct.pastDue.text"),
            cta: t("acct.billingCta"),
          }
        : trial?.state === "trialing" && Number.isInteger(daysLeft)
          ? {
              testid: "admin-trial-banner",
              tone: "bg-sky-100 text-sky-900 border-sky-300",
              Icon: Clock,
              text: daysLeft === 0 ? t("acct.trial.lastDay") : t("acct.trial.text", { days: daysLeft }),
              cta: t("acct.trial.cta"),
            }
          : null;
  if (!banner) return null;
  return (
    <div
      data-testid={banner.testid}
      className={`px-6 py-2.5 flex items-center justify-between gap-4 text-sm border-b ${banner.tone}`}
    >
      <span className="flex items-center gap-2 min-w-0">
        <banner.Icon size={16} className="shrink-0" />
        <span className="truncate">{banner.text}</span>
      </span>
      {/* A plain anchor, like the super-admin bar's own link: the billing page is the
          recovery path and must resolve on the platform host AND on a tenant host, where
          this layout is mounted by a second, independent route tree (tenant/TenantSite.jsx). */}
      <a
        href={adminHref("/admin/billing")}
        data-testid="admin-account-billing-link"
        className="font-semibold underline whitespace-nowrap shrink-0"
      >
        {banner.cta}
      </a>
    </div>
  );
};

/**
 * The phone's only permanent chrome is this button: the sidebar (and its unread pill) is
 * `hidden md:block`, so without a mark HERE a practitioner on a phone learns of a waiting
 * message from no page at all — measured by browser QA on 2026-09-15 (a message waiting,
 * header text ''). Same number the sidebar shows, from the same provider.
 */
function MobileMenuButton({ onOpen }) {
  const { unreadCount } = useConversations();
  return (
    <button
      onClick={onOpen}
      data-testid="admin-mobile-menu-btn"
      className="relative p-2 rounded-xl border border-eh-border-soft text-eh-ink hover:bg-eh-bg transition"
      aria-label="Open navigation menu"
    >
      <Menu size={20} />
      {unreadCount > 0 && (
        <span
          data-testid="admin-mobile-menu-unread"
          className="absolute -top-1.5 -right-1.5 min-w-[1.1rem] px-1 py-px rounded-full bg-eh-ochre-deep text-eh-bg text-[10px] font-semibold text-center leading-4"
        >
          {unreadCount}
        </span>
      )}
    </button>
  );
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  // Whether this session is a platform operator here (GET /super-admin/check), and the
  // display name of the practice the console runs on (GET /saas/my-tenant): together they
  // make the "you are managing {practice}" banner.
  const [operatorHere, setOperatorHere] = React.useState(false);
  const [practiceName, setPracticeName] = React.useState(null);
  const [brandLogo, setBrandLogo] = React.useState(null);
  const [siteUrl, setSiteUrl] = React.useState("/");
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [account, setAccount] = React.useState({ status: null, reason: null, trial: null });
  // crm-module BEGIN C3.02 — the inbox poll below is owner-only server-side
  // (server.require_admin, decision 5); a helper's session must never schedule it.
  const isOwner = normaliseRole(user?.tenant_role) === "owner";
  // crm-module END

  React.useEffect(() => {
    // Branding, public address and account state of the current tenant. What the plan
    // includes is NOT read here: EntitlementsProvider reads the capability map.
    import("@/lib/api").then(({ api, getCurrentTenantSlug }) => {
      const slug = getCurrentTenantSlug?.() || "";
      api
        .get(TRIAL_PATHS.myTenant)
        .then((r) => {
          setBrandLogo(r.data?.branding?.logo_url || "/brand/solunary-logo.png");
          setPracticeName(tenantDisplayName(r.data) || slug || null);
          setSiteUrl(tenantSiteUrl(r.data) || `/t/${r.data?.slug || slug}`);
          setAccount({
            status: r.data?.status || null,
            reason: r.data?.suspended_reason || null,
            trial: r.data?.trial || null,
          });
        })
        .catch(() => {
          setBrandLogo("/brand/solunary-logo.png");
          setPracticeName(slug || null);
        });
      api
        .get("/super-admin/check")
        .then((r) => setOperatorHere(r.data?.is_super_admin === true))
        .catch(() => setOperatorHere(false));
    });
  }, []);

  const doLogout = async () => {
    await logout();
    navigate(adminHref("/admin/login"), { replace: true });
  };

  return (
    // The inbox is polled ONCE for the whole console, here: the sidebar renders twice
    // (desktop column and mobile drawer) and the Messages screen renders below `<Outlet/>`,
    // and all three must show the same unread count — three independent polls of the same
    // endpoint would let a badge and the list it points at disagree. The capability map
    // is shared the same way, for the same reason: the nav and the route guards below
    // `<Outlet/>` must answer "is this in my plan?" from one response.
    <EntitlementsProvider>
      <ConversationsProvider enabled={isOwner}>
        <div className="min-h-screen bg-eh-bg flex flex-col md:flex-row" data-testid="admin-layout">
          {/* Mobile Header Bar */}
          <header className="md:hidden flex items-center justify-between px-4 py-3 bg-eh-cream border-b border-eh-border-soft sticky top-0 z-30">
            <div className="flex items-center gap-3">
              <MobileMenuButton onOpen={() => setMobileOpen(true)} />
              <img src={brandLogo || "/brand/solunary-logo.png"} alt="Logo" className="h-8 w-auto object-contain" />
            </div>
          </header>

          {/* Desktop Sidebar.
              BUG-017 — PINNED TO THE VIEWPORT, AND ITS OWN SCROLLER.
              The nav is 36 links plus 7 section headings on a practice with every module on, so
              the `<aside>` is ~2000px tall: taller than a laptop viewport by itself. This wrapper
              used to be a bare `hidden md:block`, so the sidebar was simply a very tall column in
              a `min-h-screen` document — the DOCUMENT scrolled, and reading to the bottom of any
              long page took the whole navigation off the top of the screen with it.
              `md:sticky md:top-0 md:h-screen` pins it and bounds it to one viewport, which is what
              finally makes the `overflow-y-auto` the sidebar's own `<nav>` has always carried do
              something: the links scroll inside the sidebar, the brand block and the
              logout/footer block stay put, and every item stays reachable at any page scroll.
              Deliberately NOT a viewport-height shell (`h-screen overflow-hidden` on the root with
              `<main>` as the scroller): that moves the page's scroller off the document, and
              lib/SmoothScroll.jsx runs Lenis on `window` with `allowNestedScroll` off, so the main
              content would stop responding to the wheel. Sticky leaves the document as the
              scroller and changes nothing about how the content scrolls. */}
          <div className="hidden md:block md:sticky md:top-0 md:h-screen">
            <AdminSidebar brandLogo={brandLogo} user={user} onLogout={doLogout} siteUrl={siteUrl} />
          </div>

          {/* Mobile Drawer Overlay */}
          {mobileOpen && (
            <div className="fixed inset-0 z-50 md:hidden flex">
              <div
                className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm transition-opacity"
                onClick={() => setMobileOpen(false)}
              />
              <div className="relative z-10 w-72 max-w-[85vw] bg-eh-cream h-full shadow-2xl flex flex-col">
                <button
                  onClick={() => setMobileOpen(false)}
                  className="absolute top-4 right-4 p-2 rounded-xl text-eh-muted hover:text-eh-ink hover:bg-eh-bg transition z-20"
                  aria-label="Close menu"
                >
                  <X size={18} />
                </button>
                <AdminSidebar
                  brandLogo={brandLogo}
                  user={user}
                  onLogout={doLogout}
                  siteUrl={siteUrl}
                  onCloseMobile={() => setMobileOpen(false)}
                />
              </div>
            </div>
          )}

          {/* Main Content */}
          <main className="flex-1 overflow-auto min-w-0">
            <SuperAdminBanner practiceName={operatorHere ? practiceName : null} />
            <AccountStatusBanner status={account.status} reason={account.reason} trial={account.trial} />
            <div className="px-4 py-6 md:px-8 md:py-8 max-w-6xl">
              <Outlet />
            </div>
          </main>
        </div>
      </ConversationsProvider>
    </EntitlementsProvider>
  );
}
